import React, { useState, useRef, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { SearchBox, Dropdown } from 'tea-component';
import { getSearchSessionQuery } from '../../../service/service';
import { useErrorTips } from '@/pages/aigc/hooks/useErrorTips';
import { uuid } from 'tea-component/es/_util/uuid';
import CollectStarIcon from '@/pages/aigc/assets/svg/collect-star.svg';
// 移除不再需要的 ChatShareDialog 导入
import { classifyChatsByTime } from '@/pages/aigc/utils/utils';
import ChatItem from './ChatItem';
import { IHistoryChatListProps, SearchSessionQueryResponseInChats } from './_type';
import './index.scss';
import Loading from '@/pages/dashboard/components/Loadings';

/**
 * Copilot 历史会话下拉：搜索、收藏与时间分组列表。
 * 可独立作触发按钮，也可嵌入 {@link CopilotMoreMenu}（left-start 子浮窗）。
 */
const HistoryChatList: React.FC<IHistoryChatListProps> = ({
  chatList,
  bookMarkedChats,
  chatListLoading,
  onSelectSession,
  button,
  sessionId,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  conversations = [],
  open: controlledOpen,
  onOpen: onOpenCallback,
  onClose: onCloseCallback,
  className,
  overlayClassName,
  placement = 'bottom-end',
  placementOffset,
  popupContainer,
}) => {
  const { formatMessage } = useIntl();
  const showErrorTips = useErrorTips();

  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [searchResults, setSearchResults] = useState<SearchSessionQueryResponseInChats>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [showNoData, setShowNoData] = useState<boolean>(false);
  const [bubbleId, setBubbleId] = useState('');
  const [isOpen, setIsOpen] = useState<boolean>(false);

  // 移除分享弹窗相关状态
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchInputTimeout = useRef<NodeJS.Timeout>();
  const bookmarkTitleRef = useRef<HTMLDivElement>(null);
  const bookmarkDivRef = useRef<HTMLDivElement>(null);
  const chatsDivRef = useRef<HTMLDivElement>(null);

  // 计算收藏夹列表
  const showBookmarkList = useMemo(() =>
    (searchResults.length > 0) ?
      searchResults.filter(searchChat => bookMarkedChats.some(bookmarkChat => searchChat.session_id === bookmarkChat.session_id)) :
      bookMarkedChats,
  [bookMarkedChats, searchResults]
  );

  // 使用共享的时间分类工具函数

  // 获取当前显示的聊天列表
  const currentChatList = useMemo(() => {
    if (searchResults.length > 0) {
      return searchResults;
    }
    return chatList;
  }, [searchResults, chatList]);

  // 分类当前聊天列表
  const { todayItems, thisWeekItems, thisMonthItems, earlierItems } = useMemo(() =>
    classifyChatsByTime(currentChatList, bookMarkedChats),
  [currentChatList, bookMarkedChats]);

  /** 按关键词请求后端会话搜索 */
  const handleSearch = useCallback(async (keyword: string) => {
    if (!keyword.trim()) {
      setSearchResults([]);
      setShowNoData(false);
      return;
    }

    setIsSearching(true);
    setShowNoData(false);

    try {
      const data = await getSearchSessionQuery(
        {
          trace_id: uuid(),
          query: keyword.trim()
        },
        () => showErrorTips
      );
      if (data && data.length > 0) {
        setSearchResults(data);
        setShowNoData(false);
        return;
      }
      setSearchResults([]);
      setShowNoData(true);
    } catch (error) {
      setSearchResults([]);
      setShowNoData(true);
    } finally {
      setIsSearching(false);
    }
  }, [showErrorTips]);

  /** 搜索框输入，500ms 防抖后触发 handleSearch */
  const handleSearchInput = useCallback((value: string) => {
    setSearchKeyword(value);
    // searchInputTimeout 搜索优化，避免多次调用
    if (searchInputTimeout.current) {
      clearTimeout(searchInputTimeout.current);
    }

    searchInputTimeout.current = setTimeout(() => {
      handleSearch(value);
    }, 500);
  }, [handleSearch]);

  /** 清空搜索关键词与结果 */
  const handleClearSearch = useCallback(() => {
    setSearchKeyword('');
    setSearchResults([]);
    setShowNoData(false);
    if (searchInputTimeout.current) {
      clearTimeout(searchInputTimeout.current);
    }
  }, []);

  /** 选中会话并关闭下拉 */
  const handleSelectSessionClick = useCallback((selectedSessionId: string) => {
    onSelectSession(selectedSessionId);
    setIsOpen(false); // 选择会话后关闭下拉列表
  }, [onSelectSession]);

  // 鼠标悬停处理
  const onMouseEnter = (id: string) => {
    if (id === bubbleId) return;
    setBubbleId(id);
  };

  // 渲染会话列表
  const renderChatList = (items: any[], title: string) => {
    if (items.length === 0) return null;
    return (
      <div className="dba-copilot-history-chat-section">
        <div className="dba-copilot-history-chat-section-title">{title}</div>
        <div className="dba-copilot-history-chat-section-content">
          {items.map((item) => (
            <ChatItem
              key={item.session_id}
              title={item.title}
              id={item.session_id}
              active={sessionId === item.session_id}
              hasSensitiveContent={item.has_sensitive_data}
              isBookmarkItem={bookMarkedChats.some(chat => chat.session_id === item.session_id)}
              searchQuery={searchResults.length > 0 ? {
                searchKeyword: (item as any).keyword,
                searchQuery: (item as any).messages,
              } : undefined}
              onMouseEnter={onMouseEnter}
              onClick={handleSelectSessionClick}
            />
          ))}
        </div>
      </div>
    );
  };

  // 渲染下拉内容
  const renderDropdownContent = () => (
    <div className="dba-copilot-history-chat-dropdown-content">
      {/* 搜索框 */}
      <div className="dba-copilot-history-chat-search">
        <SearchBox
          ref={searchInputRef}
          placeholder={formatMessage({ id: 'dba_search_chats' })}
          value={searchKeyword}
          onClear={handleClearSearch}
          onChange={handleSearchInput}
          className="dba-copilot-dba-copilot-history-chat-search-box"
        />
      </div>

      {/* 内容区域 */}
      <div className="dba-copilot-history-chat-content" ref={chatsDivRef}>
        {
          chatListLoading ? <Loading /> : <>
            {/* 无搜索结果 */}
            {showNoData && (
              <div className="dba-copilot-history-chat-no-data">
                {formatMessage({ id: 'dba_no_search_result' })}
              </div>
            )}

            {/* 收藏夹 */}
            {!showNoData && showBookmarkList.length > 0 && (
              <section
                ref={bookmarkDivRef}
                className="dba-copilot-history-chat-bookmark-container"
                // data-is-expanded={isBookmarkExpanded} 
                // data-min-content-height={showBookmarkList.length < 4} 
              >
                <div
                  ref={bookmarkTitleRef}
                  className="dba-copilot-history-chat-bookmark-title"
                >
                  <img
                    src={CollectStarIcon}
                    alt=""
                    className="dba-copilot-history-chat-svg-icon"
                  />
                  {formatMessage({ id: 'dba_archive_title' })}
                </div>

                <div className="dba-copilot-history-chat-menu-content-wrapper">
                  {showBookmarkList.map((item) => (
                    <ChatItem
                      key={item.session_id}
                      title={item.title}
                      id={item.session_id}
                      active={sessionId === item.session_id}
                      hasSensitiveContent={item.has_sensitive_data}
                      isBookmarkItem={true}
                      searchQuery={searchResults.length > 0 ? {
                        searchKeyword: (item as any).keyword,
                        searchQuery: (item as any).messages,
                      } : undefined}
                      onMouseEnter={onMouseEnter}
                      onClick={handleSelectSessionClick}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* 时间分类列表 */}
            {!showNoData && (
              <>
                {renderChatList(todayItems, formatMessage({ id: 'dba_menu_today' }))}
                {renderChatList(thisWeekItems, formatMessage({ id: 'dba_menu_week' }))}
                {renderChatList(thisMonthItems, formatMessage({ id: 'dba_menu_month' }))}
                {renderChatList(earlierItems, formatMessage({ id: 'dba_menu_early' }))}
              </>
            )}

            {/* 空状态 */}
            {!isSearching && !showNoData && currentChatList.length === 0 && (
              <div className="dba-copilot-history-chat-empty">
                <span>{formatMessage({ id: 'dba_copilot_historical_conversations_no_data' })}</span>
              </div>
            )}
          </>
        }
        
      </div>
    </div>
  );

  const isControlled = controlledOpen !== undefined;
  const dropdownOpen = isControlled ? controlledOpen : isOpen;

  /** 打开下拉时重置搜索态 */
  const resetSearchState = useCallback((): void => {
    setSearchResults([]);
    setShowNoData(false);
    setSearchKeyword('');
    if (searchInputTimeout.current) {
      clearTimeout(searchInputTimeout.current);
    }
  }, []);

  /** 同步受控/非受控 open，打开时清空上次搜索 */
  const handleDropdownOpenChange = useCallback(
    (visible: boolean): void => {
      if (!isControlled) {
        setIsOpen(visible);
      }
      if (visible) {
        resetSearchState();
        onOpenCallback?.();
      } else {
        onCloseCallback?.();
      }
    },
    [isControlled, onCloseCallback, onOpenCallback, resetSearchState],
  );

  return (
    <Dropdown
      className={classNames('dba-copilot-history-chat-dropdown', className)}
      overlayClassName={classNames(
        'dba-copilot-history-chat-dropdown-overlay',
        overlayClassName,
      )}
      placement={placement}
      placementOffset={placementOffset}
      popupContainer={popupContainer}
      clickClose={false}
      closeOnScroll={false}
      open={dropdownOpen}
      onOpenChange={handleDropdownOpenChange}
      button={button as any}
    >
      {renderDropdownContent()}
    </Dropdown>
  );
};

export default HistoryChatList;
